import * as amplitude from '@amplitude/analytics-browser';
import { Identify } from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { getAnalyticsConfig } from './config';
import type { UserProperties } from './types';

const AMPLITUDE_INIT_FLAG = '__amplitudeInitialized';

type WindowWithAmplitudeInit = Window & {
  [AMPLITUDE_INIT_FLAG]?: boolean;
};

const isClient = () => typeof window !== 'undefined';

const hasAmplitudeInitialized = () => {
  if (!isClient()) {
    return false;
  }

  return Boolean((window as WindowWithAmplitudeInit)[AMPLITUDE_INIT_FLAG]);
};

const setAmplitudeInitialized = () => {
  (window as WindowWithAmplitudeInit)[AMPLITUDE_INIT_FLAG] = true;
};

export const initializeAmplitude = () => {
  if (!isClient() || hasAmplitudeInitialized()) {
    return;
  }

  const config = getAnalyticsConfig();

  if (!config.amplitudeApiKey) {
    console.warn('[Amplitude] No API key configured — skipping initialization');
    return;
  }

  amplitude.add(
    sessionReplayPlugin({
      sampleRate: config.amplitudeSessionReplaySampleRate,
    }),
  );
  amplitude.init(config.amplitudeApiKey, {
    autocapture: true,
    serverZone: 'EU',
  });
  setAmplitudeInitialized();
};

export const trackEvent = (eventName: string, eventProperties?: Record<string, unknown>) => {
  if (!isClient()) {
    return;
  }

  initializeAmplitude();
  amplitude.track(eventName, eventProperties);
};

export const trackPageView = (pathname: string) => {
  trackEvent('page_view', { pathname });
};

export const identifyUser = (userId: string | number, properties: UserProperties) => {
  if (!isClient()) return;
  initializeAmplitude();

  amplitude.setUserId(String(userId));

  const identify = new Identify();
  Object.entries(properties).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      identify.set(key, value);
    }
  });

  amplitude.identify(identify);
};

export const resetUser = () => {
  if (!isClient()) return;
  amplitude.reset();
};
